<template>
  <div
    role="button"
    tabindex="0"
    :aria-label="props.accessibilityLabel || undefined"
    :class="['relative w-full', props.displayInline ? 'inline-block' : 'block']"
  >
    <div
      v-if="$slots.label && labeltop"
      class="mb-1 block font-semibold"
    >
      <slot name="label" />
    </div>
    <template v-if="$slots.icon">
      <span class="absolute top-0 left-0 py-2 h-full flex items-center ml-3">
        <slot name="icon" />
      </span>
    </template>
    <div
      :class="[{'pl-11 pr-6': $slots.icon},
               {'px-4': !$slots.icon && !xs},
               {'py-3 min-h-[51.59px]': normal && !$slots.label},
               {'py-2 ': slim && !$slots.label},
               {'py-1 px-1': xs && !labeltop && !$slots.icon},
               {'py-3 text-lg min-h-[55px]': normal && labeltop && $slots.label},
               {'py-3 text-base': slim && labeltop && $slots.label},
               {'pt-6 pb-1 text-base font-semibold min-h-[55px] peer': normal && labelinside && $slots.label},
               {'pt-3 pb-1 text-base font-semibold peer': slim && labelinside && $slots.label},
               {'rounded-xl w-full': !props.inputstyle && !ruesSearchbar && !searchresultSearchbar},
               {'border-neutral-200 bg-white': !error && !ruesSearchbar && !searchresultSearchbar},
               {'border-cta-700 bg-cta-50': error && !ruesSearchbar && !searchresultSearchbar},
               {'py-4 min-h-[51.59px]': ruesSearchbar && !$slots.label},
               {'py-4 min-h-[48px]': searchresultSearchbar && !$slots.label},
               {'pt-6 pb-1 text-lg font-semibold min-h-[62px] peer w-full': (ruesSearchbar || searchresultSearchbar) && labelinside && $slots.label},
               {'lg:pt-5 lg:min-h-[46px]': searchresultSearchbar && labelinside && $slots.label},
               {'border text-left focus:outline-hidden focus:border-petrol-500 focus:ring-1 focus:ring-petrol-500 block active-input' : !ruesSearchbar && !searchresultSearchbar},
               {'border-0 text-left focus:outline-hidden focus:ring-1 focus:ring-transparent focus:bg-neutral-50 block placeholder:text-neutral-900 active-input-search' : ruesSearchbar || searchresultSearchbar},
               'flex items-center',props.inputstyle]"
    >
      <div
        v-if="$slots.label && labelinside"
        :class="[
          {'absolute -mt-3 ml-4': labelup && !ruesSearchbar},
          {'absolute text-base text-petrol-500 font-semibold duration-200 transform top-4 z-10 origin-left left-11 scale-100 -translate-y-1': (slim && !props.modelValue) && !labelup},
          {'absolute text-base text-petrol-500 font-semibold duration-200 transform top-4 z-10 origin-left left-11 scale-[75%] -translate-y-3.5': (slim && props.modelValue) && !labelup},
          {'absolute text-base text-petrol-500 font-semibold duration-200 transform top-4 z-10 origin-left left-11 scale-100 translate-y-0': (normal && !props.modelValue) && !labelup},
          {'absolute text-base text-petrol-500 font-semibold duration-200 transform top-4 z-10 origin-left left-11 -translate-y-2.5 scale-[85%]': (normal && props.modelValue) && !labelup},
          {'absolute text-lg font-normal text-neutral-900 duration-200 transform -translate-y-2 scale-[80%] z-10 origin-left left-11': ruesSearchbar || searchresultSearchbar},
          {'top-4': (ruesSearchbar || searchresultSearchbar)},
          {'lg:-translate-y-2.5 lg:top-2.5': searchresultSearchbar},
          'left-0'
        ]"
      >
        <slot name="label" />
      </div>
      <template v-if="props.clearButton">
        <span
          class="absolute top-0 right-2 py-2 ml-3 h-full flex items-center z-40"
        >
          <button
            type="button"
            class="p-1 bg-neutral-50 rounded-lg cursor-pointer aspect-square"
            :aria-label="t('clear_aria_label')"
            @click.stop="clearInput"
          >
            <UiIcon src="/svg/icons/cancel.svg" width="24" height="24" class="fill-neutral-800 rotate-180" aria-hidden="true" />
          </button>
        </span>
      </template>
      <div v-if="props.modelValue" class="truncate input-text-width">
        {{ props.modelValue }}
      </div>
      <div v-if="!props.modelValue && (ruesSearchbar || searchresultSearchbar)" class="truncate input-text-width">
        <slot name="placeholder" />
      </div>
    </div>
  </div>
</template>
<script setup lang="ts">
import type { DesignVariant } from '~/types/ui/DesignVariant'
import { isDesignVariant } from '~/types/ui/DesignVariant'

const props = defineProps({
  design: {
    type: String as PropType<DesignVariant>,
    default: 'normal',
    validator: (value: string) => isDesignVariant(value)
  },
  label: {
    type: String,
    default: ''
  },
  labelPosition: {
    type: String,
    default: 'inside'
  },
  accessibilityLabel: {
    type: String,
    default: ''
  },
  inputstyle: {
    type: [String, Array],
    default: ''
  },
  placeholder: {
    type: String,
    default: ' '
  },
  name: {
    type: String,
    default: ''
  },
  type: {
    type: String,
    default: 'text'
  },
  title: {
    type: String,
    default: ''
  },
  modelValue: {
    type: String,
    default: ''
  },
  error: {
    type: Boolean,
    default: false
  },
  autocomplete: {
    type: Boolean,
    default: true
  },
  clearButton: {
    type: Boolean,
    default: false
  },
  displayInline: {
    type: Boolean,
    default: true
  }
})
const { translate: t } = useTranslations('UiFakeInput')
const normal = computed(() => props.design === 'normal')
const slim = computed(() => props.design === 'slim')
const xs = computed(() => props.design === 'xs')
const ruesSearchbar = computed(() => props.design === 'ruesSearchbar')
const searchresultSearchbar = computed(() => props.design === 'searchresultSearchbar')

// Label choices
const labelup = computed(() => props.label === 'up')
const labeltop = computed(() => props.labelPosition === 'top')
const labelinside = computed(() => props.labelPosition === 'inside')

const emit = defineEmits<{(e: 'update:modelValue', value: string): void}>()

const selectedValue = ref(props.modelValue)
const clearInput = () => {
  emit('update:modelValue', (selectedValue.value = ''))
}
watch(() => props.modelValue, (newValue) => {
  selectedValue.value = newValue
})
</script>

<style scoped>
@reference "~/assets/css/main.css";
.input-text-width {
  width: calc(100% - 1.25rem);
}
.active .active-input{
  @apply outline-hidden border-petrol-500 ring-1 ring-petrol-500
}
.active .active-input-search{
  @apply outline-hidden bg-neutral-25 rounded-none lg:rounded-xl
}
</style>
